import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Clock, Building, Smartphone, Banknote, CheckCircle, Copy, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const PaymentPendingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderData, paymentMethod } = location.state || {};

  if (!orderData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p>Aucune commande en attente</p>
            <Button className="mt-4" onClick={() => navigate('/')}>
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { order_id, total, display_total, currency, payment_instructions, payment_deadline } = orderData;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  const getPaymentIcon = () => {
    switch (paymentMethod) {
      case 'interac': return <Building className="w-8 h-8 text-primary" />;
      case 'orange_money': return <Smartphone className="w-8 h-8 text-orange-500" />;
      case 'cash': return <Banknote className="w-8 h-8 text-green-500" />;
      default: return <Clock className="w-8 h-8 text-yellow-500" />;
    }
  };

  const getPaymentTitle = () => {
    switch (paymentMethod) {
      case 'interac': return 'Virement Interac';
      case 'orange_money': return 'Orange Money';
      case 'cash': return 'Paiement en espèces';
      default: return 'Paiement en attente';
    }
  };

  const formatDeadline = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-CA', { 
      dateStyle: 'medium', 
      timeStyle: 'short' 
    });
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Success Header */}
        <Card className="card-dashboard border-green-500/30 bg-green-500/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-green-500">Commande créée !</h1>
                <p className="text-muted-foreground">
                  Référence: <span className="font-mono font-bold">{order_id?.slice(0, 8).toUpperCase()}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Instructions */}
        <Card className="card-dashboard">
          <CardHeader>
            <div className="flex items-center gap-3">
              {getPaymentIcon()}
              <div>
                <CardTitle>{getPaymentTitle()}</CardTitle>
                <CardDescription>Instructions de paiement</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Amount */}
            <div className="text-center p-4 bg-primary/10 rounded-lg">
              <p className="text-sm text-muted-foreground">Montant à payer</p>
              <p className="text-3xl font-bold text-primary">
                {display_total?.toFixed(2) || total?.toFixed(2)} {currency || 'CAD'}
              </p>
            </div>

            {/* Deadline Warning */}
            <div className="flex items-center gap-3 p-4 bg-yellow-500/10 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-500" />
              <div>
                <p className="font-medium text-yellow-600 dark:text-yellow-400">
                  Délai de paiement: 24 heures
                </p>
                <p className="text-sm text-muted-foreground">
                  Date limite: {payment_deadline ? formatDeadline(payment_deadline) : 'N/A'}
                </p>
              </div>
            </div>

            {/* Interac Instructions */}
            {paymentMethod === 'interac' && payment_instructions && (
              <div className="space-y-4 p-4 bg-primary/10 rounded-lg">
                <h3 className="font-semibold text-primary">
                  Instructions de virement Interac
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Envoyer à:</p>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 bg-background p-2 rounded font-mono text-sm">
                        {payment_instructions.email}
                      </code>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyToClipboard(payment_instructions.email)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Mot de passe:</p>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 bg-background p-2 rounded font-mono text-sm font-bold">
                        {payment_instructions.password}
                      </code>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyToClipboard(payment_instructions.password)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Orange Money Instructions */}
            {paymentMethod === 'orange_money' && payment_instructions && (
              <div className="space-y-4 p-4 bg-orange-500/10 rounded-lg">
                <h3 className="font-semibold text-orange-600 dark:text-orange-400">
                  Instructions Orange Money
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Envoyer au numéro:</p>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 bg-background p-2 rounded font-mono text-lg">
                        {payment_instructions.phone}
                      </code>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyToClipboard(payment_instructions.phone)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Nom du bénéficiaire:</p>
                    <p className="font-bold text-lg">{payment_instructions.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Référence à mentionner:</p>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 bg-background p-2 rounded font-mono text-lg font-bold">
                        {payment_instructions.reference}
                      </code>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyToClipboard(payment_instructions.reference)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                    Envoyez un SMS au {payment_instructions.phone} avec la référence de votre transaction Orange Money.
                  </p>
                </div>
              </div>
            )}

            {/* Cash Instructions */}
            {paymentMethod === 'cash' && payment_instructions && (
              <div className="space-y-4 p-4 bg-green-500/10 rounded-lg">
                <h3 className="font-semibold text-green-600 dark:text-green-400">
                  Instructions de paiement en espèces
                </h3>
                <p>{payment_instructions.message}</p>
                <div>
                  <p className="text-sm text-muted-foreground">Votre référence:</p>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 bg-background p-2 rounded font-mono text-lg font-bold">
                      {payment_instructions.reference || order_id?.slice(0, 8).toUpperCase()}
                    </code>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => copyToClipboard(payment_instructions.reference || order_id?.slice(0, 8).toUpperCase())}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Important Notice */}
            <div className="p-4 bg-red-500/10 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">
                <strong>Important:</strong> Votre commande sera automatiquement annulée si le paiement n'est pas reçu dans les 24 heures.
                Conservez la référence de votre paiement pour le suivi.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            className="flex-1"
            variant="outline"
            onClick={() => navigate('/buyer')}
          >
            Voir mes commandes
          </Button>
          <Button 
            className="flex-1 btn-primary"
            onClick={() => navigate('/')}
          >
            Continuer mes achats
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentPendingPage;
